/** Materialise a template group for a tenant.
 *
 * Pick a tenant, provide one or more access tags per argument alias (existing
 * tags via autocomplete, free text creates tags by name), preview how many
 * groups each template will generate, run, and review the result + warnings.
 * Materialisation is an idempotent upsert — re-running adds missing
 * combination groups and refreshes existing managed ones, never deletes.
 */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { rbacApi, type AgroupTemplateGroup, type MaterialisationResult } from '@/api/rbac';
import * as topsApi from '@/api/tourOperators';
import type { TourOperatorListItem } from '@/api/tourOperators';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { accessGroupIdActions } from '@/features/users/userLinks';

import { AgroupArgumentsForm } from './AgroupArgumentsForm';
import { WarningList } from './WarningList';

interface Props {
  group: AgroupTemplateGroup;
  onClose: () => void;
}

export function MaterialiseDialog({ group, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [top, setTop] = useState<TourOperatorListItem | null>(null);
  const [values, setValues] = useState<Record<string, string[]>>({});
  const [result, setResult] = useState<MaterialisationResult | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['agroupTemplates', 'system', group.id],
    queryFn: () => rbacApi.listTemplates({ scope: 'system', group: group.id }),
  });
  const templates = useMemo(() => templatesQuery.data?.results ?? [], [templatesQuery.data]);
  const aliases = useMemo(() => {
    const out: string[] = [];
    for (const tpl of templates) {
      for (const arg of tpl.arguments) {
        if (!out.includes(arg.alias)) out.push(arg.alias);
      }
    }
    return out;
  }, [templates]);

  const complete = top !== null && aliases.every((a) => (values[a] ?? []).length > 0);

  const mutation = useMutation({
    mutationFn: () => rbacApi.materialise(group.id, { tour_operator: top!.id, values }),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['agroupTemplates'] });
      qc.invalidateQueries({ queryKey: ['accessGroups'] });
      snackbar.show(
        `Materialised: ${res.created_group_ids.length} groups created, ${res.updated_group_ids.length} updated`,
        res.warnings.length ? 'warning' : 'success',
      );
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Materialise “{group.name}”</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <RemoteAutocomplete<TourOperatorListItem>
            fetchOnEmpty
            queryFn={async (q) => (await topsApi.list({ search: q })).results}
            getOptionLabel={(op) => `${op.name} (${op.code})`}
            getOptionKey={(op) => op.id}
            value={top}
            onChange={(v) => setTop(v as TourOperatorListItem | null)}
            label="Tour operator"
            placeholder="Search by name or code…"
          />

          <AgroupArgumentsForm
            aliases={aliases}
            topId={top?.id ?? null}
            values={values}
            onChange={setValues}
          />

          {templates.length > 0 && (
            <Alert severity="info" icon={false}>
              <Typography variant="subtitle2">Preview</Typography>
              {templates.map((tpl) => {
                const combos = tpl.arguments.reduce(
                  (acc, arg) => acc * Math.max((values[arg.alias] ?? []).length, 0),
                  1,
                );
                return (
                  <Typography key={tpl.id} variant="body2">
                    {tpl.name} → {tpl.arguments.length === 0 ? 1 : combos} group(s)
                  </Typography>
                );
              })}
            </Alert>
          )}

          {result && (
            <Alert severity={result.warnings.length ? 'warning' : 'success'}>
              <Typography variant="body2">
                Created {result.created_group_ids.length}, updated {result.updated_group_ids.length},
                skipped {result.skipped_group_ids.length} (unmanaged) group(s).
              </Typography>
              {/* The counts alone leave the reader hunting the grid for what
                  just happened — the ids the engine returns open their groups. */}
              <GroupIds label="Created" ids={result.created_group_ids} />
              <GroupIds label="Updated" ids={result.updated_group_ids} />
              <GroupIds label="Skipped" ids={result.skipped_group_ids} />
              <WarningList warnings={result.warnings} />
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained"
          disabled={!complete || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {result ? 'Run again' : 'Materialise'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One line of "Created: #1 · #2 · #3", each id opening its access group. */
function GroupIds({ label, ids }: { label: string; ids: number[] }) {
  if (ids.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="div">
      {label}:{' '}
      {ids.map((id, i) => (
        <Typography key={id} component="span" variant="caption">
          {i > 0 && ' · '}
          <ValueActions actions={accessGroupIdActions(id)}>{`#${id}`}</ValueActions>
        </Typography>
      ))}
    </Typography>
  );
}
